import { useMutation, useQueryClient } from '@tanstack/react-query'

import { API_URL } from '@/constants/env'

import { useHandleError } from '@/hooks/common/useErrorHandler'
import { promptRequest } from '@/hooks/prompts/lib'
import { promptsQueryKeys } from '@/hooks/prompts/types'
import { useAuthInfoWithThrow } from '@/hooks/user/useAuthInfoWithThrow'

import type { UUID } from '@/types/Id'

interface UseBulkArchivePromptsArgs {
  orgId: UUID | ''
}

interface BulkArchivePromptsRequest {
  promptIds: UUID[]
  archived?: boolean
}

const MAX_PROMPTS_PER_ARCHIVE_REQUEST = 100

const archivePromptBatch = async (
  promptIds: UUID[],
  archived: boolean,
  accessToken: string | undefined | null,
): Promise<void> => {
  await promptRequest<unknown>(
    'POST',
    `${API_URL}/api/v1/prompts/bulk-archive`,
    accessToken,
    { prompt_ids: promptIds, archived },
  )
}

const bulkArchivePrompts = async (
  promptIds: UUID[],
  archived: boolean,
  accessToken: string | undefined | null,
): Promise<void> => {
  // The API accepts at most 100 ids. Keep requests sequential so a large
  // selection cannot fan out into an unbounded burst.
  for (
    let index = 0;
    index < promptIds.length;
    index += MAX_PROMPTS_PER_ARCHIVE_REQUEST
  ) {
    await archivePromptBatch(
      promptIds.slice(index, index + MAX_PROMPTS_PER_ARCHIVE_REQUEST),
      archived,
      accessToken,
    )
  }
}

export const useBulkArchivePrompts = ({ orgId }: UseBulkArchivePromptsArgs) => {
  const { accessToken } = useAuthInfoWithThrow()
  const { handleError } = useHandleError()

  const queryClient = useQueryClient()

  return useMutation<void, Error, BulkArchivePromptsRequest>({
    mutationFn: ({ promptIds, archived = true }) =>
      bulkArchivePrompts(promptIds, archived, accessToken),
    onError: (err) => {
      handleError(err)
    },
    onSettled: () => {
      // Earlier batches may already have committed if a later batch fails, so
      // always reconcile from the server instead of restoring one stale,
      // all-or-nothing optimistic snapshot.
      const listKey = promptsQueryKeys.organization({ orgId })
      return queryClient.invalidateQueries({ queryKey: listKey })
    },
  })
}
